
import { useState } from 'react';
import Button from '../base/Button';
import Card from '../base/Card';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      
      const response = await fetch('https://readdy.ai/api/form/d3dds6bbr3iihosg1ap0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as URLSearchParams)
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section className="py-20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-white mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            ¿Listo para Transformar tus Finanzas?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Únete a miles de usuarios que ya están ahorrando e invirtiendo de manera inteligente. 
            Comienza tu transformación financiera hoy mismo.
          </p>
        </div>
        
        <Card className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Empezar es Gratis
            </h3>
            <p className="text-gray-600">
              Regístrate y comienza a organizar tus finanzas en minutos
            </p>
          </div>
          
          <form 
            id="registro-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <input
                type="text"
                name="name"
                placeholder="Tu nombre completo"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
            </div>
            
            <div>
              <input
                type="email"
                name="email"
                placeholder="Tu email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
            </div>
            
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Procesando...
                </>
              ) : (
                <>
                  <i className="ri-rocket-line mr-2"></i>
                  Empezar Ahora - Gratis
                </>
              )}
            </Button>
          </form>
          
          {submitStatus === 'success' && (
            <div className="mt-4 p-3 bg-emerald-100 border border-emerald-300 rounded-lg">
              <p className="text-emerald-700 text-sm">
                <i className="ri-check-line mr-1"></i>
                ¡Registro exitoso! Te contactaremos pronto.
              </p>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
              <p className="text-red-700 text-sm">
                <i className="ri-error-warning-line mr-1"></i>
                Error al enviar. Por favor, intenta nuevamente.
              </p>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Sin compromisos • Cancela cuando quieras • Soporte 24/7
            </p>
          </div>
        </Card>
        
        {/* Beneficios adicionales */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-white">
          <div className="text-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-shield-check-line text-xl"></i>
            </div>
            <h4 className="font-semibold mb-2">100% Seguro</h4>
            <p className="text-sm text-emerald-100">Tus datos están protegidos con encriptación bancaria</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-time-line text-xl"></i>
            </div>
            <h4 className="font-semibold mb-2">Setup en 5 min</h4>
            <p className="text-sm text-emerald-100">Configura tu cuenta y comienza inmediatamente</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-customer-service-2-line text-xl"></i>
            </div>
            <h4 className="font-semibold mb-2">Soporte Expert</h4>
            <p className="text-sm text-emerald-100">Asesores financieros disponibles para ayudarte</p>
          </div>
        </div>
      </div>
    </section>
  );
}
